import { z } from 'zod';
import { IsoTimestamp, MediaRef, QuizId, SchoolId, SessionId, UserId } from './common.js';
import { Role } from './common.js';
import { MultipleAnswerScoringPolicy, NUMERIC_ANSWER_MAX_ABS, QUIZ_GRADE_LEVEL_LABEL_MAX, QUIZ_GRADE_LEVEL_MAX_SELECTIONS, QUIZ_OBSERVER_MAX, QuizRules, QuizSchedule, QuizScheduleMode, QuizClassScheduleInput, QuizStatus, CameraPolicy, StudentQuestion, IntegrityPactSnapshot, QuizTeacherPermissions, SHORT_ANSWER_MAX_ACCEPTED_ANSWERS, questionScoreLimitError, } from './quiz.js';
import { GRADE_LEVEL_NAME_MAX } from './grade-level.js';
import { SUBJECT_NAME_MAX } from './subject.js';
import { CameraAvailabilitySnapshot, CameraAvailabilityStatus, CameraConsentSnapshot, EssayAttachment, ESSAY_ATTACHMENT_MAX_BYTES, ResponseValue, SessionScheduleSnapshot, SessionStatus, StudentResponse, } from './session.js';
import { LockoutContext, UnlockDecisionChannel, UnlockRequest } from './unlock-request.js';
import { Accommodation } from './accommodation.js';
import { DEFAULT_SCHOOL_TIME_ZONE, isSupportedTimeZone } from './school.js';
export const Password = z.string().min(6).max(128);
// ---- POST /v1/admin/users (provision) --------------------------------------
export const ProvisionUserRequest = z.object({
    email: z.string().email(),
    password: Password,
    displayName: z.string().min(1).max(200),
    role: Role,
    schoolId: SchoolId,
    nip: z.string().trim().min(1).max(64).optional(),
    studentNumber: z.string().max(64).optional(),
    /** @deprecated Class Memberships are now the source of truth. Form callers may omit. */
    classroomIds: z.array(z.string().max(64)).default([]),
    /** @deprecated Teaching Assignments are now the source of truth. Form callers may omit. */
    subjectIds: z.array(z.string().trim().min(1).max(100)).max(100).default([]),
    /**
     * Fixture / disposable account; excluded from the default Users list.
     * `default(false)` applies to new provisions only — legacy Firestore docs may
     * omit this field and are treated as non-test (`!== true`), not `=== false`.
     */
    isTestData: z.boolean().default(false),
}).superRefine((user, ctx) => {
    if (user.role === 'proctor' && !user.nip) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['nip'],
            message: 'NIP / Teacher ID is required for proctors',
        });
    }
});
export const ProvisionUserResponse = z.object({
    uid: z.string(),
    email: z.string().email(),
});
// ---- POST /v1/admin/users/:uid/reset-password -------------------------------
export const ResetPasswordResponse = z.object({
    ok: z.literal(true),
    /** Shown once to the admin; never persisted or logged in plaintext. */
    temporaryPassword: z.string(),
});
// ---- POST /v1/auth/change-password ------------------------------------------
export const ChangePasswordRequest = z.object({
    newPassword: Password,
});
/**
 * The session that requested the change is dead on response: Firebase Auth
 * invalidates every refresh token for an account when its password changes.
 * `customToken` is the replacement session, minted best-effort — when it is
 * absent the client must sign out and send the user to sign-in, because the
 * password change itself has already committed either way.
 */
export const ChangePasswordResponse = z.object({
    ok: z.literal(true),
    customToken: z.string().optional(),
});
// ---- POST /v1/admin/quizzes (create quiz) ----------------------------------
/**
 * Optional per-question countdown, mirroring `QuestionContent.durationSec`.
 * Declared on every answerable admin variant so a quiz save round-trip does not
 * silently drop it (quiz-versioning rebuilds each question document from the
 * parsed payload).
 */
const questionDurationSec = z.number().int().positive().optional();
const AdminMcqQuestion = z
    .object({
    questionBankRef: z.string().max(128).optional(),
    id: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/).optional(),
    type: z.literal('mcq'),
    prompt: z.string().min(1).max(10000),
    mediaRefs: z.array(MediaRef).max(8).optional(),
    stimulusGroupId: z.string().min(1).max(64).optional(),
    stimulus: z.string().max(20000).optional(),
    durationSec: questionDurationSec,
    options: z
        .array(z.object({
        id: z.string().min(1).max(8),
        text: z.string().min(1).max(2000),
        mediaRefs: z.array(MediaRef).max(4).optional(),
    }))
        .min(2)
        .max(8),
    correctOptionId: z.string().min(1).max(8),
    maxScore: z.number().int().positive(),
    rubricNotes: z.string().max(2000).optional(),
})
    .superRefine((question, ctx) => {
    const optionIds = question.options.map((option) => option.id);
    if (new Set(optionIds).size !== optionIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['options'],
            message: 'option IDs must be unique',
        });
    }
    if (!optionIds.includes(question.correctOptionId)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['correctOptionId'],
            message: 'correct option must reference an existing option',
        });
    }
});
const AdminMultipleAnswerQuestion = z
    .object({
    questionBankRef: z.string().max(128).optional(),
    id: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/).optional(),
    type: z.literal('multipleAnswer'),
    prompt: z.string().min(1).max(10000),
    mediaRefs: z.array(MediaRef).max(8).optional(),
    stimulusGroupId: z.string().min(1).max(64).optional(),
    stimulus: z.string().max(20000).optional(),
    durationSec: questionDurationSec,
    options: z
        .array(z.object({
        id: z.string().min(1).max(8),
        text: z.string().min(1).max(2000),
        mediaRefs: z.array(MediaRef).max(4).optional(),
    }))
        .min(2)
        .max(8),
    correctOptionIds: z.array(z.string().min(1).max(8)).min(1).max(8),
    scoringPolicy: MultipleAnswerScoringPolicy.default('allOrNothing'),
    maxScore: z.number().int().positive(),
    rubricNotes: z.string().max(2000).optional(),
})
    .superRefine((question, ctx) => {
    const optionIds = question.options.map((option) => option.id);
    if (new Set(optionIds).size !== optionIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['options'],
            message: 'option IDs must be unique',
        });
    }
    if (new Set(question.correctOptionIds).size !== question.correctOptionIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['correctOptionIds'],
            message: 'correct option IDs must be unique',
        });
    }
    if (question.correctOptionIds.some((id) => !optionIds.includes(id))) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['correctOptionIds'],
            message: 'correct options must reference existing options',
        });
    }
});
const AdminEssayQuestion = z.object({
    questionBankRef: z.string().max(128).optional(),
    id: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/).optional(),
    type: z.literal('essay'),
    prompt: z.string().min(1).max(10000),
    mediaRefs: z.array(MediaRef).max(8).optional(),
    stimulusGroupId: z.string().min(1).max(64).optional(),
    stimulus: z.string().max(20000).optional(),
    durationSec: questionDurationSec,
    maxScore: z.number().int().positive(),
    rubricNotes: z.string().max(2000).optional(),
});
const AdminTrueFalseQuestion = z.object({
    questionBankRef: z.string().max(128).optional(),
    id: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/).optional(),
    type: z.literal('trueFalse'),
    prompt: z.string().min(1).max(10000),
    mediaRefs: z.array(MediaRef).max(8).optional(),
    stimulusGroupId: z.string().min(1).max(64).optional(),
    stimulus: z.string().max(20000).optional(),
    durationSec: questionDurationSec,
    correctValue: z.boolean(),
    maxScore: z.number().int().positive(),
    rubricNotes: z.string().max(2000).optional(),
});
const AdminShortAnswerQuestion = z.object({
    questionBankRef: z.string().max(128).optional(),
    id: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/).optional(),
    type: z.literal('shortAnswer'),
    prompt: z.string().min(1).max(10000),
    mediaRefs: z.array(MediaRef).max(8).optional(),
    stimulusGroupId: z.string().min(1).max(64).optional(),
    stimulus: z.string().max(20000).optional(),
    durationSec: questionDurationSec,
    // '|' is reserved by the Excel workbook exchange format; see ShortAnswerQuestion in quiz.ts
    acceptedAnswers: z.array(z.string().trim().min(1).max(500).regex(/^[^|]*$/, "accepted answers must not contain '|'")).min(1).max(SHORT_ANSWER_MAX_ACCEPTED_ANSWERS),
    caseSensitive: z.boolean().default(false),
    maxScore: z.number().int().positive(),
    rubricNotes: z.string().max(2000).optional(),
});
const AdminMatchingQuestion = z.object({
    questionBankRef: z.string().max(128).optional(),
    id: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/).optional(),
    type: z.literal('matching'),
    prompt: z.string().min(1).max(10000),
    mediaRefs: z.array(MediaRef).max(8).optional(),
    stimulusGroupId: z.string().min(1).max(64).optional(),
    stimulus: z.string().max(20000).optional(),
    durationSec: questionDurationSec,
    pairs: z.array(z.object({
        leftId: z.string().trim().min(1).max(32),
        leftText: z.string().trim().min(1).max(2000),
        rightId: z.string().trim().min(1).max(32),
        rightText: z.string().trim().min(1).max(2000),
    })).min(2).max(50),
    correctPairs: z.array(z.object({
        leftId: z.string().trim().min(1).max(32),
        rightId: z.string().trim().min(1).max(32),
    })).min(1).max(50),
    scoringPolicy: MultipleAnswerScoringPolicy.default('allOrNothing'),
    maxScore: z.number().int().positive(),
    rubricNotes: z.string().max(2000).optional(),
}).superRefine((question, ctx) => {
    const leftIds = question.pairs.map((pair) => pair.leftId);
    const rightIds = question.pairs.map((pair) => pair.rightId);
    const correctByLeft = new Map(question.correctPairs.map((pair) => [pair.leftId, pair.rightId]));
    if (new Set(leftIds).size !== leftIds.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pairs'], message: 'matching left IDs must be unique' });
    }
    if (new Set(rightIds).size !== rightIds.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pairs'], message: 'matching right IDs must be unique' });
    }
    if (correctByLeft.size !== question.pairs.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['correctPairs'], message: 'every matching left item needs one solution' });
    }
    if (question.correctPairs.some((pair) => !leftIds.includes(pair.leftId) || !rightIds.includes(pair.rightId))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['correctPairs'], message: 'matching solutions must reference existing pair IDs' });
    }
    const solutionRightIds = [...correctByLeft.values()];
    if (new Set(solutionRightIds).size !== solutionRightIds.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['correctPairs'], message: 'matching solutions must use each right item once' });
    }
});
const AdminNumericQuestion = z.object({
    questionBankRef: z.string().max(128).optional(),
    id: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/).optional(),
    type: z.literal('numeric'),
    prompt: z.string().min(1).max(10000),
    mediaRefs: z.array(MediaRef).max(8).optional(),
    stimulusGroupId: z.string().min(1).max(64).optional(),
    stimulus: z.string().max(20000).optional(),
    durationSec: questionDurationSec,
    correctAnswer: z.number().finite().min(-NUMERIC_ANSWER_MAX_ABS).max(NUMERIC_ANSWER_MAX_ABS),
    tolerance: z.number().finite().nonnegative().max(NUMERIC_ANSWER_MAX_ABS).default(0),
    maxScore: z.number().int().positive(),
    rubricNotes: z.string().max(2000).optional(),
});
const AdminOrderingQuestion = z.object({
    questionBankRef: z.string().max(128).optional(),
    id: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/).optional(),
    type: z.literal('ordering'),
    prompt: z.string().min(1).max(10000),
    mediaRefs: z.array(MediaRef).max(8).optional(),
    stimulusGroupId: z.string().min(1).max(64).optional(),
    stimulus: z.string().max(20000).optional(),
    durationSec: questionDurationSec,
    items: z.array(z.object({
        id: z.string().trim().min(1).max(32),
        text: z.string().trim().min(1).max(2000),
    })).min(2).max(20).refine((items) => new Set(items.map((item) => item.id)).size === items.length, 'ordering item IDs must be unique'),
    scoringPolicy: MultipleAnswerScoringPolicy.default('allOrNothing'),
    maxScore: z.number().int().positive(),
    rubricNotes: z.string().max(2000).optional(),
});
const AdminLabelContentQuestion = z.object({
    questionBankRef: z.string().max(128).optional(),
    id: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/).optional(),
    type: z.literal('labelContent'),
    prompt: z.string().min(1).max(10000),
    mediaRefs: z.array(MediaRef).max(8).optional(),
    stimulusGroupId: z.string().min(1).max(64).optional(),
    stimulus: z.string().max(20000).optional(),
    maxScore: z.literal(0),
});
export const AdminQuizQuestion = z.union([
    AdminMcqQuestion,
    AdminMultipleAnswerQuestion,
    AdminEssayQuestion,
    AdminTrueFalseQuestion,
    AdminShortAnswerQuestion,
    AdminNumericQuestion,
    AdminMatchingQuestion,
    AdminOrderingQuestion,
    AdminLabelContentQuestion,
]);
const questionIdsUnique = (questions, ctx) => {
    const existingIds = questions.flatMap((question) => question.id ? [question.id] : []);
    if (new Set(existingIds).size !== existingIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'question IDs must be unique',
        });
    }
};
const AdminQuizQuestionsForCreate = z
    .array(AdminQuizQuestion)
    .max(100)
    .superRefine(questionIdsUnique);
const AdminQuizQuestions = z
    .array(AdminQuizQuestion)
    .min(1)
    .max(100)
    .superRefine(questionIdsUnique);
const QuizEligibility = z.object({
    eligibleClassroomIds: z.array(z.string().min(1).max(64)).max(100).optional(),
    eligibleUserIds: z.array(UserId).max(500).optional(),
    proctorUserIds: z.array(UserId).max(QUIZ_OBSERVER_MAX).optional(),
});
const QuizCollaboration = z.object({
    sharedAuthorIds: z.array(UserId).max(100).optional(),
    allowedSubjectIds: z.array(z.string().min(1).max(SUBJECT_NAME_MAX)).max(100).optional(),
    allowedClassroomIds: z.array(z.string().min(1).max(64)).max(100).optional(),
    /** Admin-only: teacher access switches. The teacher routes reject this field. */
    teacherPermissions: QuizTeacherPermissions.optional(),
});
export const CreateQuizRequest = z.object({
    title: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    subject: z.string().max(SUBJECT_NAME_MAX).optional(),
    gradeLevel: z.string().max(QUIZ_GRADE_LEVEL_LABEL_MAX).optional(),
    subjectId: z.string().min(1).max(64).nullable().optional(),
    gradeLevelIds: z.array(z.string().min(1).max(64)).max(QUIZ_GRADE_LEVEL_MAX_SELECTIONS).optional(),
    gradeLevelNames: z.array(z.string().min(1).max(GRADE_LEVEL_NAME_MAX)).max(QUIZ_GRADE_LEVEL_MAX_SELECTIONS).optional(),
    kind: z.enum(['exam', 'quiz']),
    scheduleMode: QuizScheduleMode.default('global'),
    schedule: QuizSchedule,
    classSchedules: z.array(QuizClassScheduleInput).max(100).optional(),
    rules: QuizRules.optional(),
    questions: AdminQuizQuestionsForCreate,
})
    .superRefine((request, ctx) => {
    const message = questionScoreLimitError(request.rules?.limitQuestionScoreTo100 === true, request.questions);
    if (message)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['questions'], message });
})
    .and(QuizEligibility)
    .and(QuizCollaboration);
export const UpdateQuizRequest = z
    .object({
    /** Version read by the authoring client; prevents lost concurrent edits. */
    expectedVersion: z.number().int().positive().optional(),
    reason: z.string().max(500).optional(),
    status: QuizStatus.optional(),
    title: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    subject: z.string().max(SUBJECT_NAME_MAX).nullable().optional(),
    gradeLevel: z.string().max(QUIZ_GRADE_LEVEL_LABEL_MAX).nullable().optional(),
    subjectId: z.string().min(1).max(64).nullable().optional(),
    gradeLevelIds: z.array(z.string().min(1).max(64)).max(QUIZ_GRADE_LEVEL_MAX_SELECTIONS).optional(),
    gradeLevelNames: z.array(z.string().min(1).max(GRADE_LEVEL_NAME_MAX)).max(QUIZ_GRADE_LEVEL_MAX_SELECTIONS).optional(),
    kind: z.enum(['exam', 'quiz']).optional(),
    scheduleMode: QuizScheduleMode.optional(),
    schedule: QuizSchedule.optional(),
    classSchedules: z.array(QuizClassScheduleInput).max(100).optional(),
    rules: QuizRules.optional(),
    questions: AdminQuizQuestions.optional(),
})
    .and(QuizEligibility)
    .and(QuizCollaboration);
export const QuizListQuery = z.object({
    ownership: z.enum(['mine', 'shared', 'imported', 'all']).default('all'),
    subject: z.string().trim().max(100).optional(),
    classroomId: z.string().trim().max(64).optional(),
    status: QuizStatus.optional(),
    schedule: z.enum(['scheduled', 'ended']).optional(),
});
export const IntegrityPactAcknowledgementRequest = z.object({
    decision: z.enum(['acknowledge', 'deny']),
    version: z.string().trim().min(1).max(32),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
});
export const IntegrityPactReceipt = IntegrityPactSnapshot.pick({
    version: true,
    contentHash: true,
    acknowledgedAt: true,
    acknowledgedBy: true,
    acknowledgedRole: true,
    expiresAt: true,
}).extend({ acknowledgementId: z.string().min(1).max(64) });
export const IntegrityPactPreflightResponse = z.object({
    required: z.boolean(),
    passcodeEnabled: z.boolean().default(true),
    pact: IntegrityPactSnapshot.pick({
        version: true,
        contentHash: true,
        title: true,
        body: true,
        acknowledgementText: true,
        expiresAt: true,
        updatedAt: true,
    }).optional(),
    acknowledgement: IntegrityPactReceipt.optional(),
});
export const CreateQuizResponse = z.object({
    quizId: QuizId,
    passcodeSeedRef: z.string(),
});
// ---- POST /v1/sessions/start -----------------------------------------------
export const SessionStartResponse = z.object({
    sessionId: SessionId,
    sessionJwt: z.string(),
    status: SessionStatus,
    startedAt: IsoTimestamp,
    submittedAt: IsoTimestamp.optional(),
    serverTimeMs: z.number().int().nonnegative(),
    durationSec: z.number().int().positive(),
    remainingSec: z.number().int().nonnegative(),
    endsAt: IsoTimestamp,
    /** Whether the student must submit a passcode before starting. */
    passcodeEnabled: z.boolean().default(true),
    /**
     * The student's accommodations, frozen on the session when it was created.
     * `durationSec`, `remainingSec` and `endsAt` already include `extraTimeSec`.
     */
    accommodations: Accommodation.default({}),
    lockout: LockoutContext.nullable().default(null),
    cameraConsent: CameraConsentSnapshot.optional(),
    /** The server-resolved class window; never contains another student's assignment. */
    scheduleSnapshot: SessionScheduleSnapshot.nullable().optional(),
    /**
     * Absolute, server-stamped deadlines for per-question countdowns, keyed by
     * question id. Only timed questions appear. A resume returns the same values,
     * so reloading the page cannot restart or extend a running countdown.
     */
    questionDeadlines: z.record(z.string(), IsoTimestamp).default({}),
    /** Questions whose countdown already ran out; their answers are locked. */
    timedOutQuestionIds: z.array(z.string()).default([]),
    /**
     * The server-side cursor for this attempt. A resume on a fresh device must
     * land on the page containing it — under sequential mode any earlier page
     * refuses writes, so starting at index 0 would strand the attempt.
     */
    currentQuestionIndex: z.number().int().min(0).default(0),
    /**
     * First question index of the furthest page the student has written on.
     * Pages strictly below it are committed — under allowRedo:false the answers
     * left behind are locked. Absent on sessions written before the flag
     * shipped; defaults to 0 (nothing committed).
     */
    committedThroughIndex: z.number().int().min(0).default(0),
    /**
     * The no-redo commit ledger: for each page index, the sequence of the
     * most recent write that landed on a later page. A saved answer locks
     * when its page's entry exceeds the response's `valueSeq` — a skipped
     * question answered after its page committed stays editable until a
     * newer forward write lands.
     */
    commitSeqByPage: z.record(z.string(), z.number().int().nonnegative()).default({}),
    bundle: z.object({
        quizTitle: z.string(),
        rules: QuizRules,
        questions: z.array(StudentQuestion),
        responses: z.array(StudentResponse),
    }),
});
// ---- GET /v1/sessions/eligible -------------------------------------------
export const EligibleQuiz = z.object({
    id: QuizId,
    title: z.string(),
    subject: z.string().nullable().optional(),
    gradeLevel: z.string().nullable().optional(),
    kind: z.enum(['exam', 'quiz']),
    /** `ended` marks a quiz whose governing schedule has passed; it stays
     * listed as history (with the retained attempt status) until archived. */
    status: z.enum(['scheduled', 'live', 'ended']),
    schedule: QuizSchedule,
    attemptStatus: SessionStatus.nullable(),
    /**
     * A make-up retake exists but starting it would exceed the quiz's
     * maxAttempts cap — the card must not offer a Start that /start refuses.
     */
    attemptCapReached: z.boolean().optional(),
    result: z.object({
        totalScore: z.number().nonnegative(),
        maxScore: z.number().positive(),
    }).optional(),
});
export const EligibleQuizzesResponse = z.object({
    quizzes: z.array(EligibleQuiz),
    serverTimeMs: z.number().int().nonnegative(),
});
// ---- Student receipts, history, and settings -------------------------------
export const PublishedStudentResult = z.object({
    totalScore: z.number().nonnegative(),
    maxScore: z.number().positive(),
});
const StudentReceiptBase = z.object({
    attemptId: SessionId,
    quizId: QuizId,
    quizTitle: z.string(),
    scheduledStartAt: IsoTimestamp,
    startedAt: IsoTimestamp,
    submittedAt: IsoTimestamp,
    answeredCount: z.number().int().nonnegative(),
    totalQuestions: z.number().int().nonnegative(),
    timeUsedSec: z.number().int().nonnegative(),
    gradingExpectation: z.string(),
});
export const StudentReceiptResponse = z.discriminatedUnion('resultStatus', [
    StudentReceiptBase.extend({ resultStatus: z.literal('pending') }),
    StudentReceiptBase.extend({
        resultStatus: z.literal('published'),
        result: PublishedStudentResult,
    }),
]);
const StudentHistoryBase = z.object({
    attemptId: SessionId,
    quizId: QuizId,
    quizTitle: z.string(),
    subject: z.string().nullable().optional(),
    submittedAt: IsoTimestamp,
});
export const StudentHistoryItem = z.discriminatedUnion('resultStatus', [
    StudentHistoryBase.extend({ resultStatus: z.literal('pending') }),
    StudentHistoryBase.extend({
        resultStatus: z.literal('published'),
        result: PublishedStudentResult,
    }),
]);
export const StudentHistoryResponse = z.object({
    attempts: z.array(StudentHistoryItem),
});
export const StudentPreferences = z.object({
    timerWarnings: z.boolean(),
    keyboardShortcuts: z.boolean(),
});
export const StudentSettingsResponse = z.object({
    profile: z.object({
        displayName: z.string(),
        email: z.string().email().nullable(),
        studentNumber: z.string().nullable(),
        classrooms: z.array(z.object({
            id: z.string(),
            name: z.string(),
            gradeLevel: z.string().nullable(),
        })),
    }),
    preferences: StudentPreferences,
});
export const StudentSettingsUpdateRequest = StudentPreferences.partial().refine((value) => value.timerWarnings !== undefined || value.keyboardShortcuts !== undefined, { message: 'at least one preference is required' });
// ---- PUT /v1/sessions/:id/responses/:questionId --------------------------
export const StudentResponseWriteRequest = z.object({
    value: ResponseValue.optional(),
    answeredAtElapsedMs: z.number().int().nonnegative(),
    isDoubtful: z.boolean().default(false),
    seq: z.number().int().positive(),
    questionIndex: z.number().int().nonnegative(),
});
export const StudentResponseWriteResponse = z.object({
    response: StudentResponse,
    /**
     * The session's per-question deadlines after this write. A write anchors the
     * countdown for the question it lands on, and only that question: a write is
     * an autosave, not navigation. The next question's countdown starts when the
     * student enters it and the client announces arrival.
     */
    questionDeadlines: z.record(z.string(), IsoTimestamp).default({}),
    /** Questions locked because their countdown ran out. */
    timedOutQuestionIds: z.array(z.string()).default([]),
    /**
     * First question index of the furthest page the student has written on,
     * after this write. Pages strictly below it are committed — under
     * allowRedo:false the answers left behind are locked.
     */
    committedThroughIndex: z.number().int().min(0).default(0),
    /**
     * The no-redo commit ledger after this write (see SessionStartResponse).
     * Echoed on every write result, including permanent refusals, so the
     * client can mirror exactly which answers the server considers locked.
     */
    commitSeqByPage: z.record(z.string(), z.number().int().nonnegative()).default({}),
});
// ---- POST /v1/sessions/:id/essay-attachments -----------------------------
export const StudentEssayAttachmentRequest = z.object({
    questionId: z.string().min(1).max(64),
    fileName: z.string().trim().min(1).max(200),
    mimeType: z.string().trim().min(1).max(100),
    byteSize: z.number().int().positive().max(ESSAY_ATTACHMENT_MAX_BYTES),
});
export const StudentEssayAttachmentResponse = z.object({
    attachment: EssayAttachment,
    uploadUrl: z.string().url(),
    method: z.literal('PUT'),
    expiresAt: IsoTimestamp,
});
// ---- POST /v1/sessions/:id/heartbeat ---------------------------------------
/**
 * The student client reports what its own device sees. `detectedAt` is
 * deliberately absent: the API stamps it, so a proctor never reads a clock the
 * student controls.
 */
export const SessionHeartbeatRequest = z.object({
    cameraAvailability: z.object({
        status: CameraAvailabilityStatus,
    }).optional(),
    /**
     * Delivery acknowledgement for a room broadcast. The client reports the id it
     * currently renders as its banner, once per broadcast — not on every tick.
     * Optional so a client that omits it (and every pre-existing client) behaves
     * exactly as before.
     */
    broadcastAck: z.object({
        id: z.string().uuid(),
    }).optional(),
    /**
     * Answers still waiting in the device outbox. The proctor reads it next to a
     * stale heartbeat as "offline but answering". Optional for older clients.
     */
    pendingAnswers: z.number().int().nonnegative().optional(),
});
export const SessionHeartbeatResponse = z.object({
    lastHeartbeatAt: IsoTimestamp,
    cameraAvailability: CameraAvailabilitySnapshot.optional(),
});
// ---- POST /v1/sessions/:id/submit ------------------------------------------
export const SessionSubmitRequest = z.object({
    clientElapsedMs: z.number().int().nonnegative(),
    /** Web offline client proves every locally allocated response was acknowledged first. */
    lastResponseSeq: z.number().int().nonnegative().optional(),
});
export const SessionSubmitResponse = z.object({
    status: SessionStatus,
    submittedAt: IsoTimestamp,
    answeredCount: z.number().int().nonnegative(),
    flaggedCount: z.number().int().nonnegative(),
    totalQuestions: z.number().int().nonnegative(),
});
// ---- POST /v1/sessions/:id/violation ---------------------------------------
export const ViolationRequest = z.object({
    type: z.enum([
        'visibility_hidden',
        'visibility_visible',
        'fullscreen_exit',
        'fullscreen_enter',
        'fullscreen_reentry_blocked',
        'picture_in_picture_enter',
        'picture_in_picture_leave',
        'split_screen',
        'tab_blur',
        'tab_focus',
        'paste',
        'reconnect',
        'time_drift',
    ]),
    details: z.record(z.string(), z.unknown()).default({}),
});
// ---- GET /v1/proctor/exams/:quizId/violation-log ----------------------------
/**
 * AIMSIS-20932 — the proctor's timestamped violation log download.
 *
 * `sessionIds` is an optional comma-separated list; omitted means every
 * session of the exam (the "finished exam" case), capped server-side. The
 * same route serves the per-student export the drawer issues for one id.
 */
export const ViolationLogExportQuery = z.object({
    format: z.enum(['csv', 'xlsx', 'pdf']).default('csv'),
    sessionIds: z.string().trim().max(8000).optional(),
});
// ---- GET /v1/proctor/quizzes/:id/passcode ----------------------------------
export const ProctorPasscodeResponse = z.object({
    passcode: z.string().regex(/^\d{6}$/),
    /** Seconds until the code rotates; null when the credential is static. */
    rotatesInSec: z.number().int().nonnegative().nullable(),
    /** `class` = the per-class exam password (AIMSIS-20924); `totp` = quiz TOTP. */
    source: z.enum(['totp', 'class']).default('totp'),
    classroomId: z.string().min(1).max(64).optional(),
});
// ---- POST /v1/proctor/quizzes/:id/class-password -----------------------------
export const ProctorClassPasswordResetRequest = z.object({
    classroomId: z.string().min(1).max(64),
    /** Explicit new password; omitted mints a random six-digit one. */
    password: z.string().regex(/^\d{6}$/).optional(),
});
// ---- GET /v1/proctor/exams ------------------------------------------------
export const ProctorExamStats = z.object({
    online: z.number().int().nonnegative().nullable(),
    locked: z.number().int().nonnegative().nullable(),
    pending: z.number().int().nonnegative().nullable(),
});
export const ProctorExam = z.object({
    id: QuizId,
    title: z.string(),
    subject: z.string().nullable(),
    gradeLevel: z.string().nullable(),
    status: z.enum(['scheduled', 'live', 'closed']),
    schedule: QuizSchedule,
    classSchedules: z.array(z.object({
        classroomId: z.string().min(1).max(64),
        name: z.string().min(1).max(200).optional(),
        gradeLevel: z.string().max(32).optional(),
        schedule: QuizSchedule,
        sourceScheduleId: z.string().min(1).max(160).optional(),
        source: z.object({
            system: z.enum(['aimsis', 'cbt']),
            examId: z.string().min(1).max(64).optional(),
            syncedAt: IsoTimestamp.optional(),
        }).optional(),
    })).max(100).default([]),
    scope: z.enum(['global', 'class', 'mixed']),
    room: z.string().nullable(),
    participantCount: z.number().int().nonnegative().nullable(),
    stats: ProctorExamStats,
    archivedAt: IsoTimestamp.nullable(),
    // Governing make-up window for a closed exam: the active window if one is
    // running, else the nearest upcoming scheduled window. Null otherwise.
    makeUpWindow: z.object({
        startAt: IsoTimestamp,
        endAt: IsoTimestamp,
        durationSec: z.number().int().positive(),
    }).nullable().optional(),
    // Distinct students holding a usable make-up window on this exam (any
    // status) — drives the "Make Up Session" badge on the exam card.
    makeUpStudentCount: z.number().int().nonnegative().default(0),
});
export const ProctorExamsResponse = z.object({
    exams: z.array(ProctorExam),
    serverTimeMs: z.number().int().nonnegative(),
    /**
     * The tenant's configured timezone (`schools/{schoolId}.meta.timeZone`), so
     * the picker labels each window in the school's own zone instead of assuming
     * one. Validated as an IANA zone: a bad value would otherwise throw inside a
     * formatter and blank the page.
     */
    timeZone: z.string()
        .refine(isSupportedTimeZone, { message: 'unsupported timezone' })
        .default(DEFAULT_SCHOOL_TIME_ZONE),
});
// ---- GET /v1/proctor/exams/:id/monitor ----------------------------------
export const ProctorMonitorStudent = z.object({
    id: UserId,
    identityIds: z.array(z.string().min(1).max(128)).min(1),
    displayName: z.string().min(1).max(200),
    studentNumber: z.string().max(64).nullable(),
});
export const ProctorMonitorResponse = z.object({
    students: z.array(ProctorMonitorStudent).max(1000),
    expectedCount: z.number().int().nonnegative(),
    /**
     * Student ids holding an active access lock (`status=scheduled`,
     * `lockReason!=null`) on this quiz. The lock survives a scheduled make-up
     * window, so a make-up student stays listed until the proctor unlocks them.
     */
    accessLockedStudentIds: z.array(UserId).max(1000).default([]),
    /**
     * The governing make-up window per student on this quiz — an active or
     * upcoming scheduled window (`status=scheduled`, `lockReason=null`), at
     * most one entry per `studentId`. Students whose make-up already ended
     * (consumed or lapsed windows) get no entry and fall back to their attempt
     * state. The proctor monitor groups the listed students under Make Up
     * Session instead of Finished/Not started and shows the saved window on
     * the card.
     */
    makeUpWindows: z.array(z.object({
        studentId: UserId,
        startAt: z.string().optional(),
        endAt: z.string().optional(),
        createdAt: z.string().optional(),
    })).max(1000).default([]),
    /**
     * Non-default accommodations keyed by the student's canonical uid (the same
     * id `PUT /exams/:quizId/accommodations/:studentUid` stores them under).
     */
    accommodations: z.record(z.string(), Accommodation).default({}),
    /**
     * The exam's camera policy, so the proctor can judge compliance without a
     * second request. Defaults to `disabled` when the quiz has no policy.
     */
    cameraPolicy: CameraPolicy.pick({
        mode: true,
        consentVersion: true,
        captureEnabled: true,
    }).optional(),
    sessions: z.array(z.object({
        id: SessionId,
        participantId: UserId,
        studentUid: UserId.optional(),
        studentName: z.string().nullable().optional(),
        studentNumber: z.string().nullable().optional(),
        classroomId: z.string().nullable().optional(),
        status: SessionStatus,
        startedAt: IsoTimestamp.optional(),
        submittedAt: IsoTimestamp.optional(),
        effectiveEndsAt: IsoTimestamp.optional(),
        lockoutEngagedAt: IsoTimestamp.optional(),
        lockoutReason: z.string().nullable().optional(),
        lastHeartbeatAt: IsoTimestamp.optional(),
        lastResponseSeq: z.number().int().nonnegative().optional(),
        pendingAnswers: z.number().int().nonnegative().optional(),
        answeredCount: z.number().int().nonnegative().optional(),
        totalQuestions: z.number().int().nonnegative().optional(),
        flaggedCount: z.number().int().nonnegative().optional(),
        cameraConsent: CameraConsentSnapshot.optional(),
        cameraAvailability: CameraAvailabilitySnapshot.optional(),
        scheduleSnapshot: SessionScheduleSnapshot.optional(),
        lineageRootSessionId: SessionId.optional(),
        attemptNumber: z.number().int().positive().optional(),
        invalidated: z.boolean().optional(),
    })).max(500).default([]),
});
// ---- PUT /v1/proctor/exams/:quizId/accommodations/:studentUid ---------------
export const ProctorAccommodationResponse = z.object({
    ok: z.literal(true),
    accommodations: Accommodation,
});
// ---- GET/POST /v1/proctor/exams/:id/unlock-requests ---------------------
export const ProctorUnlockMetrics = z.object({
    pending: z.number().int().nonnegative(),
    approvedToday: z.number().int().nonnegative(),
    deniedToday: z.number().int().nonnegative(),
    averageResponseSec: z.number().int().nonnegative().nullable(),
});
export const ProctorUnlockInboxResponse = z.object({
    pending: z.array(UnlockRequest).max(1000),
    resolvedToday: z.array(UnlockRequest).max(1000),
    metrics: ProctorUnlockMetrics,
    serverTimeMs: z.number().int().nonnegative(),
});
// ---- GET /v1/proctor/unlock-inbox ----------------------------------------
/**
 * The global unlock inbox: every pending unlock request across the quizzes a
 * proctor is assigned to, grouped by quiz. The per-quiz inbox stays available
 * for a single exam; this shape answers "what is waiting for me anywhere".
 */
export const ProctorUnlockInboxQuizOption = z.object({
    id: QuizId,
    title: z.string().min(1).max(200),
    status: QuizStatus,
});
export const ProctorUnlockInboxGroup = z.object({
    quizId: QuizId,
    quizTitle: z.string().min(1).max(200),
    quizStatus: QuizStatus,
    /** Pending requests for this quiz, oldest first. */
    pending: z.array(UnlockRequest).max(500),
});
export const ProctorUnlockInboxTotals = z.object({
    pending: z.number().int().nonnegative(),
    quizzesWithPending: z.number().int().nonnegative(),
    approvedToday: z.number().int().nonnegative(),
    deniedToday: z.number().int().nonnegative(),
    averageResponseSec: z.number().int().nonnegative().nullable(),
    /** Creation stamp of the longest-waiting pending request, for the wait clock. */
    oldestPendingAt: IsoTimestamp.nullable(),
});
export const ProctorGlobalUnlockInboxResponse = z.object({
    groups: z.array(ProctorUnlockInboxGroup).max(200),
    /** Every quiz assigned to this proctor, so the client can offer the scope filter. */
    quizzes: z.array(ProctorUnlockInboxQuizOption).max(200),
    /** Each request already carries its exam title, so no quiz pairing is needed. */
    resolvedToday: z.array(UnlockRequest).max(200),
    totals: ProctorUnlockInboxTotals,
    /**
     * True when a server-side scan cap was reached, so the client can say the
     * list is incomplete instead of implying the queue is empty.
     */
    truncated: z.boolean(),
    serverTimeMs: z.number().int().nonnegative(),
});
export const ProctorUnlockEvidence = z.object({
    liveSelfieUrl: z.string().url().nullable(),
    referencePhotoUrl: z.string().url().nullable(),
    expiresAt: IsoTimestamp.nullable(),
});
export const ProctorUnlockDetailResponse = z.object({
    request: UnlockRequest,
    evidence: ProctorUnlockEvidence,
    priorViolationCount: z.number().int().nonnegative(),
    examMode: z.enum(['inRoom', 'remote']),
    settings: z.lazy(() => ProctorSettings),
    readAloudCode: z.string().regex(/^[A-Z2-9]{6}$/).nullable().optional(),
});
export const ProctorUnlockDecisionRequest = z.object({
    decision: z.enum(['approve', 'deny']),
    channel: UnlockDecisionChannel.optional(),
    reason: z.string().trim().max(500).optional(),
}).superRefine((decision, context) => {
    if (decision.decision === 'approve' && !decision.channel) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['channel'], message: 'approval channel is required' });
    }
});
export const ProctorUnlockDecisionResponse = z.object({
    outcome: z.enum(['applied', 'idempotent']),
    request: UnlockRequest,
    readAloudCode: z.string().regex(/^[A-Z2-9]{6}$/).nullable(),
});
export const ProctorBroadcastRequest = z.object({
    message: z.string().trim().min(1).max(500),
});
export const ProctorEndExamRequest = z.object({
    confirmation: z.literal('END EXAM'),
});
// ---- POST /v1/proctor/exams/:quizId/print-pack ------------------------------
export const PrintPackSection = z.enum(['cards', 'password', 'attendance', 'berita', 'score']);
/**
 * One server-rendered PDF of exam-day paperwork for a single classroom.
 *
 * `sections` defaults to all five documents; `cards` and `password` are the
 * credential sections — requesting either one mints fresh access codes for the
 * whole class roster (the same re-issue `POST /access-codes` performs), which
 * revokes every still-active unredeemed code those students hold. While the
 * exam's governing window is OPEN the caller must send
 * `confirmRegenerate: true`, or the request is refused with 409 — a mid-exam
 * re-print would invalidate the codes not-yet-started students are holding.
 * `expiresAt` overrides the minted codes' expiry, which otherwise defaults to
 * the classroom's governing schedule `endAt` (capped at the 30-day maximum
 * TTL every access-code surface enforces).
 */
export const PrintPackRequest = z.object({
    classroomId: z.string().trim().min(1).max(128),
    sections: z.array(PrintPackSection).min(1).max(5).optional(),
    expiresAt: IsoTimestamp.optional(),
    confirmRegenerate: z.boolean().optional(),
});
export const ProctorRoomCommandResponse = z.object({
    commandId: z.string().uuid(),
    affectedSessions: z.number().int().nonnegative(),
});
// ---- GET /v1/proctor/exams/:quizId/broadcasts ----------------------------
/**
 * One immutable broadcast record, newest first in the history list.
 *
 * `deliveredCount` / `readCount` / `notDeliveredCount` are computed from the
 * receipt rows on every read — never from a stored counter — so the roll-up on
 * the list and the row states in the detail view cannot disagree. `notDelivered`
 * is the actionable number for a proctor: a target that has neither a delivery
 * nor a read stamp.
 */
export const ProctorBroadcastSummary = z.object({
    id: z.string().uuid(),
    message: z.string().min(1).max(500),
    sentAt: IsoTimestamp,
    sentBy: UserId,
    /**
     * The sender's display name, frozen at send time so the history reads as a
     * name rather than a uid even after the proctor renames their profile.
     */
    sentByName: z.string().max(200).nullable(),
    targetCount: z.number().int().nonnegative(),
    /**
     * True when fewer receipt rows exist than the send targeted — i.e. a session
     * batch failed after the record was committed. The roll-up then describes only
     * the receipts that were written, and the proctor must be told rather than
     * shown a roll-up that silently omits the missing students.
     */
    partial: z.boolean().default(false),
    deliveredCount: z.number().int().nonnegative(),
    readCount: z.number().int().nonnegative(),
    notDeliveredCount: z.number().int().nonnegative(),
});
export const ProctorBroadcastListResponse = z.object({
    broadcasts: z.array(ProctorBroadcastSummary).max(100),
    nextCursor: z.string().min(1).max(128).nullable(),
    hasMore: z.boolean(),
    /**
     * True when the HISTORY scan budget was hit, so older broadcasts may be
     * missing. Reports the scan only: a per-broadcast partial write is reported by
     * `ProctorBroadcastSummary.partial`, so each warning maps to exactly one cause.
     */
    truncated: z.boolean().default(false),
});
/**
 * One row per targeted student. The target set is frozen at send time, so a
 * student who joined the room after the broadcast is correctly absent.
 */
export const ProctorBroadcastReceiptRow = z.object({
    sessionId: SessionId,
    participantId: UserId,
    studentName: z.string().max(200).nullable(),
    studentNumber: z.string().max(64).nullable(),
    classroomId: z.string().max(64).nullable(),
    deliveredAt: IsoTimestamp.nullable(),
    readAt: IsoTimestamp.nullable(),
});
export const ProctorBroadcastDetailResponse = z.object({
    broadcast: ProctorBroadcastSummary,
    receipts: z.array(ProctorBroadcastReceiptRow).max(500),
    truncated: z.boolean().default(false),
});
// ---- POST /v1/sessions/:id/broadcast-dismissals ---------------------------
export const SessionBroadcastDismissalRequest = z.object({
    broadcastId: z.string().uuid(),
});
export const SessionBroadcastDismissalResponse = z.object({
    broadcastId: z.string().uuid(),
    deliveredAt: IsoTimestamp,
    readAt: IsoTimestamp,
});
// ---- GET/PATCH /v1/proctor/settings --------------------------------------
export const ProctorSettings = z.object({
    displayName: z.string().trim().min(1).max(200),
    rooms: z.array(z.string().trim().min(1).max(80)).max(20),
    defaultRoom: z.string().trim().min(1).max(80).nullable(),
    browserNotifications: z.boolean(),
    audioAlerts: z.boolean(),
    escalationMinutes: z.union([z.literal(5), z.literal(10), z.null()]),
    unlockChannel: z.enum(['auto', 'readAloud', 'push']),
    requireDenialReason: z.boolean(),
})
    .superRefine((settings, context) => {
    if (settings.defaultRoom !== null && !settings.rooms.includes(settings.defaultRoom)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['defaultRoom'],
            message: 'default room must be one of the configured rooms',
        });
    }
});
export const ProctorSettingsResponse = z.object({
    settings: ProctorSettings,
    updatedAt: IsoTimestamp.nullable(),
});
export { Role };
//# sourceMappingURL=api.js.map